import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SubscriptionStatus } from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { euro } from '../../shared/billing';
import { useSession } from '../../shared/session';
import {
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Field,
  Input,
  Note,
  Select,
  Textarea,
} from '../admin-kit';
import { useBilling, useSubscriptions } from './master-data';

const STATUSES: SubscriptionStatus[] = ['trial', 'active', 'paused', 'cancelled'];

/**
 * Het abonnement van één organisatie (MST-6): pakket, status, proefperiode, korting en
 * facturatieadres. Onderaan wat de analyse over deze organisatie zegt, met dezelfde regels als op
 * `/master/abonnementen`.
 */
export function SubscriptionTab({
  tenantId,
  onToast,
}: {
  tenantId: string;
  onToast: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const billing = useBilling();
  const subscriptions = useSubscriptions();
  const row = billing.rows.find((entry) => entry.tenant.id === tenantId);
  // Rechtstreeks uit de abonnementen en niet uit de analyse: die laat het archief weg, en dan
  // zag een gearchiveerde organisatie "geen abonnement" en overschreef Bewaren het echte.
  const current = subscriptions.data?.find((sub) => sub.tenantId === tenantId);

  // Wat je net koos; `null` = wat er bewaard is.
  const [planId, setPlanId] = useState<string | null>(null);
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [discount, setDiscount] = useState<string | null>(null);
  const [billingEmail, setBillingEmail] = useState<string | null>(null);
  const [notes, setNotes] = useState<string | null>(null);

  const waarde = {
    planId: planId ?? current?.planId ?? billing.plans.find((plan) => plan.active)?.id ?? '',
    status: status ?? current?.status ?? 'active',
    trialEndsAt: trialEndsAt ?? current?.trialEndsAt ?? '',
    discount: discount ?? String(current?.discountPct ?? 0),
    billingEmail: billingEmail ?? current?.billingEmail ?? '',
    notes: notes ?? current?.notes ?? '',
  };

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await api.PUT('/api/v1/platform/tenants/{tenantId}/subscription', {
        params: { path: { tenantId } },
        body: {
          planId: waarde.planId,
          status: waarde.status,
          trialEndsAt: waarde.trialEndsAt || null,
          // Afgerond: het contract wil een geheel getal, en "12,5" gaf anders een kale 400.
          discountPct: Math.min(
            Math.max(Math.round(Number(waarde.discount.replace(',', '.'))) || 0, 0),
            100,
          ),
          billingEmail: waarde.billingEmail.trim() || null,
          notes: waarde.notes,
        } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      onToast(t('master.billing.subscriptionSaved'));
      setPlanId(null);
      setStatus(null);
      setTrialEndsAt(null);
      setDiscount(null);
      setBillingEmail(null);
      setNotes(null);
      await queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
  });

  if (billing.pending) return <LoadingState rows={4} label={t('common.loading')} />;
  if (billing.error) {
    return (
      <Note tone="danger" role="alert">
        {t('errors.generic')}
      </Note>
    );
  }

  return (
    <>
      <Card>
        <CardHead
          title={t('master.billing.subscription')}
          actions={
            <Btn
              size="sm"
              disabled={save.isPending || !waarde.planId}
              testId="subscription-save"
              onClick={() => save.mutate()}
            >
              {t('tenants.save')}
            </Btn>
          }
        />
        <CardPad className="a-stack">
          {current ? null : <Note tone="info">{t('master.billing.noSubscriptionYet')}</Note>}
          {save.isError ? (
            <Note tone="danger" role="alert">
              {t('errors.generic')}
            </Note>
          ) : null}
          <Field label={t('master.billing.plan')}>
            {(props) => (
              <Select
                {...props}
                data-testid="subscription-plan"
                value={waarde.planId}
                onChange={(event) => setPlanId(event.target.value)}
              >
                {billing.plans
                  .filter((plan) => plan.active || plan.id === current?.planId)
                  .map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} · {euro(plan.pricePerMonthCents)}
                      {plan.pricePerUserCents > 0
                        ? ` + ${euro(plan.pricePerUserCents)} ${t('master.billing.perUserShort')}`
                        : ''}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
          <Field label={t('master.billing.status')}>
            {(props) => (
              <Select
                {...props}
                value={waarde.status}
                onChange={(event) => setStatus(event.target.value as SubscriptionStatus)}
              >
                {STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {t(`master.billing.statuses.${value}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {waarde.status === 'trial' ? (
            <Field label={t('master.billing.trialEndsAt')}>
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={waarde.trialEndsAt}
                  onChange={(event) => setTrialEndsAt(event.target.value)}
                />
              )}
            </Field>
          ) : null}
          <Field label={t('master.billing.discount')}>
            {(props) => (
              <Input
                {...props}
                inputMode="numeric"
                value={waarde.discount}
                onChange={(event) => setDiscount(event.target.value)}
              />
            )}
          </Field>
          <Field label={t('master.billing.billingEmail')} optional>
            {(props) => (
              <Input
                {...props}
                type="email"
                value={waarde.billingEmail}
                onChange={(event) => setBillingEmail(event.target.value)}
              />
            )}
          </Field>
          <Field label={t('master.billing.notes')} optional>
            {(props) => (
              <Textarea
                {...props}
                value={waarde.notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            )}
          </Field>
        </CardPad>
      </Card>

      {row ? (
        <Card>
          <CardHead title={t('master.billing.analysis')} />
          <CardPad className="a-stack">
            <p>
              {t('master.billing.thisMonth', { amount: euro(row.mrrCents) })} ·{' '}
              {t('master.billing.activeUsers', {
                count: row.stats?.active ?? 0,
                max: row.plan?.maxUsers ?? '∞',
              })}
            </p>
            {row.signals.length === 0 ? (
              <p className="a-muted">{t('master.billing.noSignals')}</p>
            ) : (
              <ul className="a-stack">
                {row.signals.map((signal) => (
                  <li key={signal.kind}>
                    <Badge tone={signal.weight >= 80 ? 'danger' : 'attention'}>
                      {t(`master.billing.signalShort.${signal.kind}`)}
                    </Badge>{' '}
                    {t(`master.billing.signal.${signal.kind}`, signal.values)}
                  </li>
                ))}
              </ul>
            )}
            {row.suggestedPlan ? (
              <Note tone="info">
                {t('master.billing.suggest', { plan: row.suggestedPlan.name })}
              </Note>
            ) : null}
          </CardPad>
        </Card>
      ) : null}
    </>
  );
}
